import { useState, useEffect, useRef, useMemo, useTransition } from 'react'
import { useInView } from 'react-intersection-observer'
import { useQuery, useInfiniteQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Navbar } from '@/components/navbar'
import { NewsCard, NewsCardSkeleton } from '@/components/news-card'
import { FilterSidebar } from '@/components/filter-sidebar'
import { TopStoriesWidget } from '@/components/top-stories-widget'
import { newsApi } from '@/services/api'
import { Loader2, Filter } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { getViewMode, isAuthenticated } from '@/lib/utils'
import { toast } from 'sonner'

// Tabs component
function StickyTabs({ activeTab, onTabChange }) {
  return (
    <div className="sticky-tabs">
      <div className="centered-content">
        <div className="flex items-center h-14">
          <button
            onClick={() => onTabChange('feed')}
            className={`flex-1 h-full px-6 font-semibold transition-all duration-200 border-b-2 ${
              activeTab === 'feed'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-600 hover:text-primary hover:bg-primary/5'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
              </svg>
              Feed
            </div>
          </button>

          <button
            onClick={() => onTabChange('firehose')}
            className={`flex-1 h-full px-6 font-semibold transition-all duration-200 border-b-2 ${
              activeTab === 'firehose'
                ? 'border-orange-600 text-orange-600'
                : 'border-transparent text-gray-600 hover:text-orange-600 hover:bg-orange-50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Firehose
            </div>
          </button>
        </div>
      </div>
    </div>
  )
}

// Infinite scroll feed component
function InfiniteScrollFeed({ activeTab, filters, viewMode }) {
  const [isPending, startTransition] = useTransition()
  const navigate = useNavigate()
  
  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0,
    rootMargin: '200px',
  })

  // Determine which API to use based on active tab
  const fetchFunction = activeTab === 'feed' ? newsApi.getFeed : newsApi.getFirehose

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQuery({
    queryKey: [activeTab, filters],
    queryFn: ({ pageParam = 1 }) => fetchFunction(pageParam, filters),
    getNextPageParam: (lastPage) => {
      if (!lastPage.data.next) return undefined
      
      // Extract page number from URL
      try {
        const url = new URL(lastPage.data.next)
        const page = url.searchParams.get('page')
        return page ? parseInt(page, 10) : undefined
      } catch {
        return undefined
      }
    },
    enabled: true,
    staleTime: 1000 * 60 * 5, // 5 minutes
    cacheTime: 1000 * 60 * 10, // 10 minutes
    refetchOnWindowFocus: false,
  })

  // Check for subscription expiry in response
  useEffect(() => {
    if (data?.pages) {
      const firstPage = data.pages[0]
      if (firstPage?.data?.code === 'SUBSCRIPTION_EXPIRED' || firstPage?.data?.msg === 'SUBSCRIPTION_EXPIRED') {
        // Subscription expired - redirect to pricing
        toast.error('Your subscription has expired. Please renew to continue.')
        localStorage.setItem('ldirect', window.location.pathname)
        navigate('/pricing')
      }
    }
  }, [data?.pages, navigate])

  // Memoize articles to prevent unnecessary re-renders (MUST be before any returns)
  const articles = useMemo(() => {
    if (!data?.pages) return []
    
    return data.pages
      .flatMap(page => page?.data?.results || [])
      .filter(article => article != null) // Filter out null/undefined
  }, [data?.pages])

  // Load more when in view (with startTransition for smoother rendering)
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      startTransition(() => {
        fetchNextPage()
      })
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage])

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        {[...Array(5)].map((_, i) => (
          <NewsCardSkeleton key={i} variant={viewMode} />
        ))}
      </div>
    )
  }

  // Error state
  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="bg-red-50 rounded-full p-8 mb-6">
          <svg className="w-24 h-24 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-2xl font-bold text-gray-800 mb-2">Something went wrong</h3>
        <p className="text-gray-600 text-center max-w-md mb-6">
          We couldn't load the articles. Please try again.
        </p>
        <Button onClick={() => window.location.reload()}>
          Retry
        </Button>
      </div>
    )
  }

  // Empty state
  if (articles.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-full p-8 mb-6">
          <svg className="w-24 h-24 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
          </svg>
        </div>
        <h3 className="text-2xl font-bold text-gray-800 mb-2">No Articles Found</h3>
        <p className="text-gray-600 text-center max-w-md mb-6">
          We couldn't find any articles matching your filters. Try adjusting your search criteria.
        </p>
        <Button onClick={() => window.location.reload()}>
          Refresh Page
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {articles.map((article, index) => (
        <NewsCard
          key={article?.alias || article?.id || `article-${index}`}
          article={article}
          variant={viewMode}
          index={index}
        />
      ))}

      {/* Loading next page skeletons - show during fetch AND during render */}
      {(isFetchingNextPage || isPending) && (
        <>
          {[...Array(3)].map((_, i) => (
            <NewsCardSkeleton key={`skeleton-${i}`} variant={viewMode} />
          ))}
        </>
      )}

      {/* Load more trigger */}
      <div ref={loadMoreRef} className="py-4">
        {!hasNextPage && articles.length > 0 && (
          <p className="text-sm text-gray-500 text-center">You've reached the end</p>
        )}
      </div>
    </div>
  )
}

// Main HomePage component
export function HomePage() {
  const [activeTab, setActiveTab] = useState('feed')
  const [filterOpen, setFilterOpen] = useState(false)
  const [viewMode, setViewMode] = useState(getViewMode())
  const [filters, setFilters] = useState(null)

  const scrollPositions = useRef({ feed: 0, firehose: 0 })

  // Force card view on mobile
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768
  const effectiveViewMode = isMobile ? 'card' : viewMode

  const handleViewModeChange = (mode) => {
    setViewMode(mode)
  }

  const handleFilterChange = (newFilters) => {
    console.log('Filters changed:', newFilters)
    setFilters(newFilters)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleTabChange = (newTab) => {
    // Save current scroll position
    scrollPositions.current[activeTab] = window.scrollY

    // Switch tab
    setActiveTab(newTab)

    // Restore scroll position after a brief delay
    setTimeout(() => {
      window.scrollTo({
        top: scrollPositions.current[newTab],
        behavior: 'instant',
      })
    }, 0)
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navbar
        onViewModeChange={setViewMode}
        onFilterToggle={() => setFilterOpen(!filterOpen)}
        showFilterButton={true}
      />

      <StickyTabs activeTab={activeTab} onTabChange={handleTabChange} />

      {/* Three-pane layout */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Sidebar - Filters (Desktop) */}
          <aside className="hidden lg:block lg:col-span-3">
            <div className="sticky-sidebar custom-scrollbar">
              <FilterSidebar
                onFilterChange={handleFilterChange}
                activeTab={activeTab}
              />
            </div>
          </aside>

          {/* Center Content - News Feed */}
          <main className="lg:col-span-6">
            <InfiniteScrollFeed
              activeTab={activeTab}
              filters={filters}
              viewMode={effectiveViewMode}
            />
          </main>

          {/* Right Sidebar - Top Stories (Desktop) */}
          <aside className="hidden lg:block lg:col-span-3">
            <div className="sticky-sidebar custom-scrollbar">
              <TopStoriesWidget />
            </div>
          </aside>
        </div>
      </div>

      {/* Mobile Filter FAB */}
      <button
        onClick={() => setFilterOpen(!filterOpen)}
        data-logo-box
        className="lg:hidden fixed right-4 bottom-4 z-40 p-4 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg hover:shadow-xl transition-all duration-300 group"
        aria-label="Open Filters"
      >
        <Filter className="w-6 h-6 group-hover:rotate-90 transition-transform duration-300" />
      </button>

      {/* Scroll to top FAB */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed right-4 bottom-20 lg:bottom-4 z-40 p-3 rounded-full bg-white border border-gray-200 text-gray-700 shadow-lg hover:shadow-xl transition-all duration-300 group"
        aria-label="Scroll to top"
      >
        <svg
          className="w-5 h-5 group-hover:-translate-y-1 transition-transform duration-300"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      </button>

      {/* Mobile Filter Drawer */}
      {filterOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/50" onClick={() => setFilterOpen(false)}>
          <div
            className="absolute right-0 top-0 bottom-0 w-80 bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 flex flex-col h-full">
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
                <h2 className="text-lg font-bold">Filters</h2>
                <button 
                  onClick={() => setFilterOpen(false)} 
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  aria-label="Close filters"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                <FilterSidebar
                  onFilterChange={handleFilterChange}
                  activeTab={activeTab}
                />
              </div>
              <div className="pt-4 border-t border-gray-200 mt-4">
                <button
                  onClick={() => setFilterOpen(false)}
                  className="w-full px-4 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
